import { useQuery, useMutation, notifyManager } from '@tanstack/react-query'
import { validate } from 'uuid'
import { useShallow } from 'zustand/react/shallow'

import {
  createTemplateAttribute,
  updateTemplateAttribute,
  deleteTemplateAttribute,
  listTemplateAttributes,
  getTemplateAttribute,
  bulkCreateTemplateAttributes,
  bulkDeleteTemplateAttributes,
  bulkUpdateTemplateAttributes,
  TemplateAttribute,
  TemplateAttributeInput,
} from '@/lib/api/client'
import queryClient from '@/queryClient'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'
import CustomError from '@/lib/api/CustomError'
import { useToast } from '@/components/ui/use-toast'
import {
  useListViewsTemplates,
  useCreateViewTemplate,
  useCreateImageTemplate,
} from '@/services/hooks/template_attributes'
import {
  DEFAULT_VIEW_HEIGHT,
  DEFAULT_VIEW_WIDTH,
} from '@/pages/CADPage/constants'
import { useTimeTravelStore } from '@/lib/timeTravel/timeTravelStore'

type mutationTemplateAttributeProps = {
  shouldOptimisticUpdate?: boolean
  isTimeTraveling?: boolean
  onSuccess?: (templateAttribute?: TemplateAttribute) => void
}

const onCreateTemplateAttributeSuccess = ({
  props,
  templateAttribute,
}: {
  props?: mutationTemplateAttributeProps
  templateAttribute: TemplateAttribute
}) => {
  const shouldOptimisticUpdate =
    props === undefined ? true : props.shouldOptimisticUpdate !== false
  if (shouldOptimisticUpdate) {
    notifyManager.batch(() => {
      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.TEMPLATE_ATTRIBUTES,
            { documentPageId: templateAttribute?.document_page },
          ],
        },
        (templateAttributes: Array<TemplateAttribute> | undefined) => {
          if (templateAttributes) {
            return [...templateAttributes, templateAttribute]
          }
          return []
        },
      )

      queryClient.setQueryData(
        [
          QUERY_KEYS.TEMPLATE_ATTRIBUTE,
          { templateAttributeId: templateAttribute?.id },
        ],
        templateAttribute,
      )
    })
  }
  if (props?.onSuccess) props.onSuccess(templateAttribute)
}

export const useCreateTemplateAttribute = (
  props?: mutationTemplateAttributeProps,
) => {
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_TEMPLATE_ATTRIBUTE],
    mutationFn: async ({
      documentPageId,
      payload,
    }: {
      documentPageId: string
      payload: TemplateAttributeInput
    }) => {
      let updatedPayload = { ...payload } as any
      if (!updatedPayload.template_values?.position?.z) {
        const templateAttributes = await listTemplateAttributesQuery({
          documentPageId,
        })

        const maxZ = Math.max(
          ...templateAttributes.map(
            (attr) => (attr.template_values as any)?.position?.z || 1000,
          ),
        )
        updatedPayload = {
          ...updatedPayload,
          template_values: {
            ...updatedPayload.template_values,
            position: {
              ...updatedPayload.template_values.position,
              z: maxZ + 1,
            },
          },
        }
      }
      const res = await createTemplateAttribute(documentPageId, updatedPayload)
      return res
    },
    onSuccess: (templateAttribute) => {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'create',
          old: {},
          new: templateAttribute,
        })
      }
      onCreateTemplateAttributeSuccess({ props, templateAttribute })
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useBulkCreateTemplateAttributes = (
  props?: mutationTemplateAttributeProps,
) => {
  const { toast } = useToast()
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )

  return useMutation({
    mutationFn: async ({
      documentPageId,
      payloads,
    }: {
      documentPageId: string
      payloads: TemplateAttributeInput[]
    }) => {
      const res = await bulkCreateTemplateAttributes(documentPageId, payloads)
      return {
        documentPageId,
        templateAttributes: res,
      }
    },
    onSuccess({ documentPageId, templateAttributes }) {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'bulk_create',
          old: {},
          new: {
            document_page: documentPageId,
            templateAttributes,
          },
        })
      }
      templateAttributes.forEach((templateAttribute) =>
        onCreateTemplateAttributeSuccess({ props, templateAttribute }),
      )
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useBulkDeleteTemplateAttributes = (
  props?: mutationTemplateAttributeProps,
) => {
  const { toast } = useToast()
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )

  return useMutation({
    mutationKey: [MUTATION_KEYS.BULK_DELETE_TEMPLATE_ATTRIBUTES],
    mutationFn: async ({
      documentPageId,
      templateAttributes,
    }: {
      documentPageId: string
      templateAttributes: TemplateAttribute[]
    }) => {
      await bulkDeleteTemplateAttributes(
        documentPageId,
        templateAttributes.map((t) => t.id) as string[],
      )
      return {
        documentPageId,
        templateAttributes,
      }
    },
    onSuccess({ documentPageId, templateAttributes }) {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'bulk_delete',
          old: {
            document_page: documentPageId,
            templateAttributes,
          },
          new: {},
        })
      }

      notifyManager.batch(() => {
        templateAttributes.forEach((templateAttribute) => {
          const templateAttributeId = templateAttribute.id

          queryClient.setQueriesData(
            {
              queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES],
            },
            (templateAttributes: Array<TemplateAttribute> | undefined) => {
              if (templateAttributes) {
                return templateAttributes.filter(
                  (currentTemplateAttribute) =>
                    currentTemplateAttribute.id !== templateAttributeId,
                )
              }
              return []
            },
          )

          queryClient.removeQueries({
            queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
          })
        })
      })
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useBulkUpdateTemplateAttributes = (
  props?: mutationTemplateAttributeProps,
) => {
  const { toast } = useToast()
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )

  return useMutation({
    mutationKey: [MUTATION_KEYS.BULK_UPDATE_TEMPLATE_ATTRIBUTES],
    mutationFn: async ({
      documentPageId,
      payloads,
    }: {
      documentPageId: string
      payloads: Array<{
        id: string
        template_values: Record<string, any>
      }>
    }) => {
      const templateAttributes = await bulkUpdateTemplateAttributes(
        documentPageId,
        payloads,
      )
      return {
        documentPageId,
        payloads,
        templateAttributes,
      }
    },
    onMutate: async ({ documentPageId, payloads }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
      })

      const previousAttributes = queryClient.getQueryData<
        Array<TemplateAttribute>
      >([QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }])
      const oldTemplateAttributes = payloads
        .map((payload) =>
          previousAttributes?.find((template) => template.id === payload.id),
        )
        .filter(Boolean) as TemplateAttribute[]

      const payloadMap = new Map(
        payloads.map((payload) => [payload.id, payload]),
      )
      notifyManager.batch(() => {
        // Optimistically merge all updated template_values into cached list data.
        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
          },
          (oldData?: Array<TemplateAttribute>) => {
            const templates = oldData || []
            if (!oldData?.length) {
              return oldData
            }
            return templates.map((template) => {
              const payload = payloadMap.get(template.id as string)
              if (!payload) return template

              const updatedTemplate = {
                ...template,
                template_values: {
                  ...template.template_values,
                  ...payload.template_values,
                },
              }

              if (!payload.template_values.strokeColor) {
                delete updatedTemplate.template_values.strokeColor
              }

              return updatedTemplate
            })
          },
        )
      })

      return { previousAttributes, oldTemplateAttributes }
    },
    onSuccess: (
      { documentPageId, templateAttributes },
      _variables,
      context?: {
        previousAttributes?: Array<TemplateAttribute>
        oldTemplateAttributes?: Array<TemplateAttribute>
      },
    ) => {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'bulk_update',
          old: {
            document_page: documentPageId,
            templateAttributes: context?.oldTemplateAttributes || [],
          },
          new: {
            document_page: documentPageId,
            templateAttributes,
          },
        })
      }

      notifyManager.batch(() => {
        const updatedTemplateById = new Map(
          templateAttributes.map((templateAttribute) => [
            templateAttribute.id as string,
            templateAttribute,
          ]),
        )

        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
          },
          (templateAttributesData?: Array<TemplateAttribute>) => {
            if (!templateAttributesData) return templateAttributesData

            return templateAttributesData.map((templateAttribute) => {
              const updated = updatedTemplateById.get(
                templateAttribute.id as string,
              )
              return updated || templateAttribute
            })
          },
        )

        templateAttributes.forEach((templateAttribute: TemplateAttribute) => {
          queryClient.setQueryData(
            [
              QUERY_KEYS.TEMPLATE_ATTRIBUTE,
              { templateAttributeId: templateAttribute.id },
            ],
            templateAttribute,
          )
        })
      })

      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
      })

      if (props?.onSuccess) props.onSuccess(templateAttributes?.[0])
    },
    onError: (
      error: any,
      variables: {
        documentPageId: string
      },
      context?: { previousAttributes?: Array<TemplateAttribute> },
    ) => {
      const documentPageId = variables?.documentPageId
      if (context?.previousAttributes && documentPageId) {
        queryClient.setQueryData(
          [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
          context.previousAttributes,
        )
      } else if (documentPageId) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
        })
      }

      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const createTemplateAttributeMutation = () => {
  return queryClient.getMutationCache().build(queryClient, {
    mutationKey: [MUTATION_KEYS.CREATE_TEMPLATE_ATTRIBUTE],
    mutationFn: async ({
      documentPageId,
      payload,
    }: {
      documentPageId: string
      payload: TemplateAttributeInput
    }) => {
      const res = await createTemplateAttribute(documentPageId, payload)
      return res
    },
    onSuccess: (templateAttribute) =>
      onCreateTemplateAttributeSuccess({ templateAttribute }),
  })
}

export const updateTemplateAttributeMutation = () => {
  return queryClient.getMutationCache().build(queryClient, {
    mutationKey: [MUTATION_KEYS.UPDATE_TEMPLATE_ATTRIBUTE],
    onMutate: async ({ templateAttributeId, documentPageId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
      })

      const previousAttributes = queryClient.getQueryData<
        Array<TemplateAttribute>
      >([QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }])

      const oldTemplateAttribute = previousAttributes?.find(
        (t) => t.id === templateAttributeId,
      )

      notifyManager.batch(() => {
        queryClient.setQueriesData(
          {
            queryKey: [
              QUERY_KEYS.TEMPLATE_ATTRIBUTES,
              {
                documentPageId: documentPageId,
              },
            ],
          },
          (oldData?: Array<TemplateAttribute>) => {
            const templates = oldData || []

            return templates.map((template) => {
              if (template.id === templateAttributeId) {
                const updatedTemplate = {
                  ...template,
                  template_values: {
                    ...template.template_values,
                    ...payload.template_values,
                  },
                }

                if (!payload.template_values.strokeColor) {
                  delete updatedTemplate.template_values.strokeColor
                }

                return updatedTemplate
              }

              return template
            })
          },
        )
      })

      return { oldTemplateAttribute }
    },
    mutationFn: async ({
      templateAttributeId,
      payload,
    }: {
      templateAttributeId: string
      documentPageId: string
      payload: TemplateAttributeInput
    }) => {
      const res = await updateTemplateAttribute(templateAttributeId, payload)
      return res
    },
    onError: (
      _error,
      variables: {
        templateAttributeId: string
        documentPageId: string
        payload: TemplateAttributeInput
      },
      context?: { oldTemplateAttribute?: TemplateAttribute },
    ) => {
      const templateAttributeId = variables?.templateAttributeId
      const documentPageId = variables?.documentPageId
      const oldTemplateAttribute = context?.oldTemplateAttribute

      if (oldTemplateAttribute && templateAttributeId && documentPageId) {
        notifyManager.batch(() => {
          queryClient.setQueriesData(
            {
              queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
            },
            (templateAttributes: Array<TemplateAttribute> | undefined) => {
              if (!templateAttributes) return []
              return templateAttributes.map((t) =>
                t.id === templateAttributeId ? oldTemplateAttribute : t,
              )
            },
          )

          queryClient.setQueryData(
            [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
            oldTemplateAttribute,
          )
        })
      } else if (templateAttributeId && documentPageId) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
        })
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
        })
      }
    },
  })
}

export const useUpdateTemplateAttribute = ({
  shouldOptimisticUpdate = true,
  ...props
}: mutationTemplateAttributeProps) => {
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_TEMPLATE_ATTRIBUTE],
    mutationFn: async ({
      templateAttributeId,
      payload,
    }: {
      templateAttributeId: string
      documentPageId: string
      payload: TemplateAttributeInput
    }) => {
      const templateAttribute = await updateTemplateAttribute(
        templateAttributeId,
        payload,
      )
      return { templateAttribute }
    },
    onMutate: async ({ templateAttributeId, documentPageId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
      })

      const previousAttributes = queryClient.getQueryData<
        Array<TemplateAttribute>
      >([QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }])

      const oldTemplateAttribute = previousAttributes?.find(
        (t) => t.id === templateAttributeId,
      )

      notifyManager.batch(() => {
        queryClient.setQueriesData(
          {
            queryKey: [
              QUERY_KEYS.TEMPLATE_ATTRIBUTES,
              {
                documentPageId: documentPageId,
              },
            ],
          },
          (oldData?: Array<TemplateAttribute>) => {
            const templates = oldData || []

            return templates.map((template) => {
              if (template.id === templateAttributeId) {
                const updatedTemplate = {
                  ...template,
                  ...payload,
                  template_values: {
                    ...template.template_values,
                    ...payload.template_values,
                  },
                }

                if (!payload.template_values.strokeColor) {
                  delete updatedTemplate.template_values.strokeColor
                }

                return updatedTemplate
              }

              return template
            })
          },
        )
      })

      return { oldTemplateAttribute }
    },
    onSuccess: ({ templateAttribute }, _, { oldTemplateAttribute }) => {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'update',
          old: oldTemplateAttribute,
          new: templateAttribute,
        })
      }

      if (shouldOptimisticUpdate) {
        if (props?.onSuccess) props.onSuccess(templateAttribute)
      }
    },
    onError: (
      error: any,
      variables: {
        templateAttributeId: string
        documentPageId: string
        payload: TemplateAttributeInput
      },
      context?: { oldTemplateAttribute?: TemplateAttribute },
    ) => {
      const templateAttributeId = variables?.templateAttributeId
      const documentPageId = variables?.documentPageId
      const oldTemplateAttribute = context?.oldTemplateAttribute

      if (oldTemplateAttribute && templateAttributeId && documentPageId) {
        notifyManager.batch(() => {
          queryClient.setQueriesData(
            {
              queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
            },
            (templateAttributes: Array<TemplateAttribute> | undefined) => {
              if (!templateAttributes) return []
              return templateAttributes.map((t) =>
                t.id === templateAttributeId ? oldTemplateAttribute : t,
              )
            },
          )

          queryClient.setQueryData(
            [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
            oldTemplateAttribute,
          )
        })
      } else if (templateAttributeId && documentPageId) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
        })
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
        })
      }

      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useDeleteTemplateAttribute = (
  props?: mutationTemplateAttributeProps,
) => {
  const addTimeTravelEvent = useTimeTravelStore(
    useShallow((state) => state.addEvent),
  )
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_TEMPLATE_ATTRIBUTE],
    mutationFn: async ({
      templateAttributeId,
      documentPageId,
    }: {
      templateAttributeId: string
      documentPageId: string
    }) => {
      const templateAttributes = await getTemplateAttributesQuery({
        documentPageId,
      })
      const oldTemplateAttribute = templateAttributes.find(
        (t) => t.id === templateAttributeId,
      )
      await deleteTemplateAttribute(templateAttributeId)
      return { templateAttributeId, oldTemplateAttribute }
    },
    onSuccess: ({ templateAttributeId, oldTemplateAttribute }) => {
      if (!props?.isTimeTraveling) {
        addTimeTravelEvent({
          source: 'template_attribute',
          action: 'delete',
          old: oldTemplateAttribute,
          new: {},
        })
      }

      notifyManager.batch(() => {
        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES],
          },
          (templateAttributes: Array<TemplateAttribute> | undefined) => {
            if (templateAttributes) {
              return templateAttributes.filter(
                (currentTemplateAttribute) =>
                  currentTemplateAttribute.id !== templateAttributeId,
              )
            }
            return []
          },
        )

        queryClient.removeQueries({
          queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
        })
      })

      if (props?.onSuccess) props.onSuccess()
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const listTemplateAttributesQuery = ({
  documentPageId,
}: {
  documentPageId: string
}) => {
  if (!validate(documentPageId)) return Promise.resolve([])
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
    queryFn: () => listTemplateAttributes(documentPageId),
  })
}

export const useListTemplateAttributes = ({
  documentPageId,
  enabled,
}: {
  documentPageId?: string | null
  enabled?: boolean
}) => {
  const { toast } = useToast()
  return useQuery({
    queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
    queryFn: async () => {
      if (!documentPageId) return []
      try {
        const templateAttributes = await listTemplateAttributes(documentPageId)
        return templateAttributes
      } catch (e: any) {
        if (e instanceof CustomError) {
          toast({
            title: 'Error',
            description: e.message,
            variant: 'destructive',
          })
        }
        throw e
      }
    },
    enabled: validate(documentPageId) && enabled,
  })
}

const getTemplateAttributeQueryFn = async ({
  templateAttributeId,
  documentPageId,
}: {
  templateAttributeId: string
  documentPageId: string
}) => {
  if (!validate(documentPageId) || !validate(templateAttributeId))
    return Promise.resolve([])
  const list = queryClient.getQueryData<Array<TemplateAttribute>>([
    QUERY_KEYS.TEMPLATE_ATTRIBUTES,
    { documentPageId },
  ])
  const fromCache = list?.find((ta) => ta.id === templateAttributeId)
  if (fromCache) return fromCache

  const templateAttribute = await getTemplateAttribute(templateAttributeId)
  return templateAttribute
}

export const getTemplateAttributesQuery = ({
  documentPageId,
}: {
  documentPageId: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTES, { documentPageId }],
    queryFn: () => listTemplateAttributes(documentPageId),
  })
}

export const getTemplateAttributeQuery = ({
  templateAttributeId,
  documentPageId,
}: {
  templateAttributeId: string
  documentPageId: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
    queryFn: () =>
      getTemplateAttributeQueryFn({ templateAttributeId, documentPageId }),
  })
}

export const useGetTemplateAttribute = ({
  templateAttributeId,
  documentPageId,
}: {
  templateAttributeId: string
  documentPageId: string
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TEMPLATE_ATTRIBUTE, { templateAttributeId }],
    queryFn: () =>
      getTemplateAttributeQueryFn({ templateAttributeId, documentPageId }),
    enabled:
      Boolean(templateAttributeId) &&
      validate(templateAttributeId) &&
      validate(documentPageId),
  })
}

export const useCreateOrAttachViewTemplateAttributeMutation = ({
  documentPageId,
}: {
  documentPageId?: string
}) => {
  const viewTemplates = useListViewsTemplates(documentPageId)
  const { mutate: updateTemplateAttribute } = useUpdateTemplateAttribute({})
  const createTemplateViewMutation = useCreateViewTemplate()

  return useMutation({
    mutationFn: async ({ viewId }: { viewId: string }) => {
      if (!documentPageId) {
        throw new Error(
          'Failed to create view template - missing document page id',
        )
      }

      const avaiableViewTemplateAttribute = viewTemplates.find(
        (attr) => attr.data_type === 'view' && !attr.template_values?.viewId,
      )
      const avaiableViewTemplateAttributeId = avaiableViewTemplateAttribute?.id

      if (avaiableViewTemplateAttributeId) {
        updateTemplateAttribute({
          documentPageId,
          templateAttributeId: avaiableViewTemplateAttributeId,
          payload: {
            data_type: 'view',
            template_values: {
              ...avaiableViewTemplateAttribute.template_values,
              viewId,
            } as any,
          },
        })
      } else {
        const rightMostView = viewTemplates
          .sort(
            (a, b) =>
              (b.template_values?.position?.x || 0) -
              (a.template_values?.position?.x || 0),
          )
          .at(viewTemplates.length - 1)

        if (rightMostView && rightMostView.template_values?.position?.x) {
          rightMostView.template_values.position.x += 1
        }

        createTemplateViewMutation.mutate({
          documentPageId,
          viewTemplateData: {
            viewId,
            type: 'view',
            size: {
              width: DEFAULT_VIEW_WIDTH,
              height: DEFAULT_VIEW_HEIGHT,
            },
            imagePosition: 'absolute',
            position: {
              x: 19.005843389601885,
              y: 279.0080638543467,
            },
          },
        })
      }
    },
  })
}

export const useCreateOrAttachImageTemplateAttributeMutation = () => {
  const { mutateAsync: updateTemplateAttribute } = useUpdateTemplateAttribute(
    {},
  )
  const { mutateAsync: createImageTemplate } = useCreateImageTemplate()

  return useMutation({
    mutationFn: async ({
      documentPageId,
      imageId,
    }: {
      documentPageId: string
      imageId: string
    }) => {
      const templateAttributes = await listTemplateAttributesQuery({
        documentPageId,
      })

      const avaiableImageTemplateAttribute = templateAttributes.find(
        (attr) => attr.data_type === 'image' && !attr.template_values?.imageId,
      )
      const avaiableImageTemplateAttributeId =
        avaiableImageTemplateAttribute?.id

      if (avaiableImageTemplateAttributeId) {
        await updateTemplateAttribute({
          documentPageId,
          templateAttributeId: avaiableImageTemplateAttributeId,
          payload: {
            data_type: 'image',
            template_values: {
              ...avaiableImageTemplateAttribute.template_values,
              imageId,
            } as any,
          },
        })
      } else {
        await createImageTemplate({
          documentPageId,
          imageData: {
            imageId,
            imagePosition: 'absolute',
          },
        })
      }
    },
  })
}

export { QUERY_KEYS, MUTATION_KEYS }
